import type {
  ActionGroupConfig,
  Application,
  LoopRecordListActionGroupConfig,
} from "@smartsuite/automation-engine-api";
import { t } from "i18next";
import { INPUT_APPLICATION_ID } from "../../../../../utils/constants";
import {
  findInputAndPick,
  pickValueConfigString,
} from "../../../../../utils/inputs";
import { findActionConfigByInstanceId } from "../../../shared/config-walk";

/**
 * Derives the loop's display name ("Repeat for each in \"<table>\"") from its
 * source when the snapshot's `loop.name` is empty (STR-14342).
 *
 * The snapshot never stores the source's human table name -- the find-records
 * action config carries only the application id, and its dynamic description
 * (where the builder reads the name) is not snapshotted. So the id is resolved
 * against the app-wide applications list (loaded per solution at startup),
 * mirroring how PhraseBuilder resolves it. Only record-list-sourced loops are
 * handled here (the design's case): the source's find-records action is located
 * by instanceId anywhere in the config tree -- including inside a Conditional
 * Logic branch -- so it does not rely on the loop being top-level or on
 * automatic_description (which omits CL-nested sources). Returns undefined for
 * outputReference / trigger sources, an unresolvable app id (cross-solution or
 * deleted table), or a missing source action -- the caller falls back to the
 * generic label.
 */
export const deriveLoopName = (
  loopConfig: LoopRecordListActionGroupConfig,
  automationActionGroups: ActionGroupConfig[],
  applications: Application[],
): string | undefined => {
  const { source } = loopConfig;
  if (source.oneofKind !== "recordList") {
    return undefined;
  }
  const { recordList } = source.recordList;
  if (recordList.oneofKind !== "actionReference") {
    return undefined;
  }

  const sourceAction = findActionConfigByInstanceId(
    automationActionGroups,
    recordList.actionReference.instanceId,
  );
  if (sourceAction === undefined) {
    return undefined;
  }

  const applicationId = findInputAndPick(
    sourceAction.inputs,
    INPUT_APPLICATION_ID,
    pickValueConfigString,
  );
  const label = applications.find(
    (application) => application.id === applicationId,
  )?.label;

  return label
    ? `${String(t("general.repeat-for-each-in"))} "${label}"`
    : undefined;
};
